/**
 * Client environment validation.
 *
 * Validates that all required Vite environment variables are present at
 * bootstrap so the app fails fast with a clear message instead of issuing
 * requests against an `undefined` backend URL or initializing Google Maps with
 * a missing key.
 *
 * Vite statically inlines `import.meta.env.VITE_*` at build time, so a missing
 * variable surfaces as an empty/undefined value here.
 */

export const REQUIRED_CLIENT_ENV = [
	"VITE_BACKEND_BASE_URL",
	"VITE_GOOGLE_API_KEY",
] as const;

export type RequiredClientEnv = (typeof REQUIRED_CLIENT_ENV)[number];

type EnvSource = Record<string, unknown>;

/**
 * Returns the list of required environment variables that are missing or blank.
 */
export const findMissingClientEnv = (
	env: EnvSource = import.meta.env
): RequiredClientEnv[] =>
	REQUIRED_CLIENT_ENV.filter((key) => {
		const value = env[key];
		return value === undefined || value === null || String(value).trim() === "";
	});

/**
 * Throws if any required environment variable is missing or blank.
 * Call once during client bootstrap.
 */
export const validateClientEnv = (env: EnvSource = import.meta.env): void => {
	const missing = findMissingClientEnv(env);
	if (missing.length > 0) {
		throw new Error(
			`Missing required environment variable(s): ${missing.join(", ")}. ` +
				`Define them in your .env file (prefixed with VITE_) before building or running the app.`
		);
	}
};
